import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, TabButton, Checkbox } from '@prosolve/common-components-ui';
import { FaPlus } from 'react-icons/fa';
import useDataAssessments from '../../../../dataHooks/useDataAssessments';
import RuleEditor from './RuleEditor';
import GradeConfigEditor from './GradeConfigEditor';
import AssessmentNamePicker from './AssessmentNamePicker';
import ModNamePicker from './ModNamePicker';

const SectionHint = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #7e7e7e;
`

const FieldLabel = styled.div`
  font-size: 14px;
  color: #565656;
`

const defaultConfig = {
  version: 1,
  enabled: false,
  mode: 'grade',
  fallbackAssessmentName: '',
  fallbackAssessmentMod: '',
  dynamic: { rules: [] },
  grade: { correctAnswers: {}, ranges: [] }
}

export default ({
  followupBranchConfig = {},
  questions = [],
  assessmentName,
  onChange,
  isReadOnly
}) => {
  // Controlled: merge defaults each render, push the whole config up on any edit
  const config = {
    ...defaultConfig,
    ...followupBranchConfig,
    dynamic: { ...defaultConfig.dynamic, ...(followupBranchConfig.dynamic || {}) },
    grade: { ...defaultConfig.grade, ...(followupBranchConfig.grade || {}) }
  };

  const update = (partial) => {
    onChange({ ...config, ...partial })
  }

  const { fetchAssessments, fetchAssessmentMods } = useDataAssessments();

  const [assessmentNames, setAssessmentNames] = useState([])

  // { [assessmentName]: [{ value: modId, text: modName }] } — mod pickers only show
  // for target assessments present in this map
  const [modsByAssessment, setModsByAssessment] = useState({})

  useEffect(() => {
    const loadAssessments = async () => {
      try {
        const assessments = await fetchAssessments() || [];
        const uniqueNames = [...new Set(assessments.map(({ name }) => name).filter(Boolean))];
        setAssessmentNames(uniqueNames)

        const mods = await fetchAssessmentMods() || [];
        const modsMap = {};
        mods.forEach(({ _id, assessmentName: modAssessmentName, modName }) => {
          if (!modAssessmentName || !_id) return;
          if (!modsMap[modAssessmentName]) {
            modsMap[modAssessmentName] = [];
          }
          modsMap[modAssessmentName].push({ value: String(_id), text: modName || String(_id) });
        });
        setModsByAssessment(modsMap)
      } catch (e) {
        console.log(e)
      }
    }
    loadAssessments()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { rules = [] } = config.dynamic;

  const onRuleChanged = (ruleIndex, updatedRule) => {
    const updatedRules = [...rules];
    updatedRules[ruleIndex] = updatedRule;
    update({ dynamic: { ...config.dynamic, rules: updatedRules } })
  }

  const onRuleDeleted = (ruleIndex) => {
    update({ dynamic: { ...config.dynamic, rules: rules.filter((r, i) => i !== ruleIndex) } })
  }

  const onRuleMoved = (fromIndex, toIndex) => {
    const updatedRules = [...rules];
    const [movedRule] = updatedRules.splice(fromIndex, 1);
    updatedRules.splice(toIndex, 0, movedRule);
    update({ dynamic: { ...config.dynamic, rules: updatedRules } })
  }

  const addNewRule = () => {
    const newRule = {
      id: String(Date.now()),
      conjunction: 'AND',
      conditions: [{ questionId: '', operator: 'any', optionIds: [] }],
      followupAssessmentName: ''
    };
    update({ dynamic: { ...config.dynamic, rules: [...rules, newRule] } })
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      rowGap: '15px'
    }}>
      <SectionHint>
        {'When enabled, a submitted assessment is analyzed and the response includes the name of a followup assessment to serve the student.'}
      </SectionHint>

      <Checkbox
        label='Enabled'
        value={config.enabled === true}
        onChange={() => {
          if (!isReadOnly) {
            update({ enabled: config.enabled !== true })
          }
        }}
        disabled={isReadOnly}
      />

      <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
        <TabButton
          $selected={config.mode === 'grade'}
          onClick={() => !isReadOnly && config.mode !== 'grade' && update({ mode: 'grade' })}
        >
          {'Grade Ranges'}
        </TabButton>
        <TabButton
          $selected={config.mode === 'dynamic'}
          onClick={() => !isReadOnly && config.mode !== 'dynamic' && update({ mode: 'dynamic' })}
        >
          {'Dynamic Rules'}
        </TabButton>
      </div>

      {
        config.mode === 'dynamic' ?
        <>
          <SectionHint>
            {'Rules are checked in order from top to bottom — the first matching rule decides the followup assessment.'}
          </SectionHint>
          {
            rules.map((rule, ruleIndex) => (
              <RuleEditor
                key={rule.id || ruleIndex}
                rule={rule}
                index={ruleIndex}
                totalRules={rules.length}
                questions={questions}
                assessmentNames={assessmentNames}
                modsByAssessment={modsByAssessment}
                currentAssessmentName={assessmentName}
                onChange={(updatedRule) => onRuleChanged(ruleIndex, updatedRule)}
                onDelete={() => onRuleDeleted(ruleIndex)}
                onMove={onRuleMoved}
                isReadOnly={isReadOnly}
              />
            ))
          }
          {
            !isReadOnly &&
            <Button style={{ display: 'inline-flex', alignSelf: 'flex-start' }} onClick={addNewRule}>
              <FaPlus style={{ fontSize: '10px', marginRight: '5px' }} />
              {'Add Rule'}
            </Button>
          }
        </>
        :
        <GradeConfigEditor
          grade={config.grade}
          questions={questions}
          assessmentNames={assessmentNames}
          modsByAssessment={modsByAssessment}
          currentAssessmentName={assessmentName}
          onChange={(updatedGrade) => update({ grade: updatedGrade })}
          isReadOnly={isReadOnly}
        />
      }

      <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px', paddingTop: '10px', borderTop: '1px solid #dedede', flexWrap: 'wrap', rowGap: '10px' }}>
        <FieldLabel>{'If nothing matches, send the student to'}</FieldLabel>
        <AssessmentNamePicker
          value={config.fallbackAssessmentName}
          assessmentNames={assessmentNames}
          excludeName={assessmentName}
          emptyText='None'
          onChange={(name) => update({ fallbackAssessmentName: name, fallbackAssessmentMod: '' })}
          disabled={isReadOnly}
        />
        {
          Boolean((modsByAssessment[config.fallbackAssessmentName] || []).length) &&
          <>
            <FieldLabel>{'with mod'}</FieldLabel>
            <ModNamePicker
              value={config.fallbackAssessmentMod}
              modOptions={modsByAssessment[config.fallbackAssessmentName]}
              onChange={(mod) => update({ fallbackAssessmentMod: mod })}
              disabled={isReadOnly}
            />
          </>
        }
      </div>
    </div>
  )
}
